import React, { useEffect, useState } from 'react';
import './CertificationList.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Snackbar, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import {
  getAllCertificationsAuth,
  enrollCertification,
  startApplication,
  createCertificationCheckoutSession,
} from '../../../api/certification';
import { setEnrollmentMap, setEnrollment } from '../../../Redux/Slices/certificationSlice';
import CertificatePreviewModal from '../CertificatePreviewModal/CertificatePreviewModal';


const BADGE_COLORS = {
  bronze: '#CD7F32',
  silver: '#A8A9AD',
  gold: '#D4AF37',
  platinum: '#6E7F80',
};

function getBadgeColor(level) {
  return BADGE_COLORS[String(level || '').toLowerCase()] || '#3BB3AE';
}

function getBadgeIconPath(badgeType) {
  const type = String(badgeType || '').toLowerCase();
  if (type === 'bronze') return '/Assets/Certification/bronze.svg';
  if (type === 'silver') return '/Assets/Certification/silver.svg';
  if (type === 'gold') return '/Assets/Certification/gold.svg';
  if (type === 'platinum') return '/Assets/Certification/platinum.svg';
  return '/Assets/Certification/bronze.svg';
}

function StatusBadge({ status }) {
  if (status === 'completed') {
    return <span className="cert-status-badge cert-status-completed"><CheckCircleIcon style={{ fontSize: 14 }} /> Certified</span>;
  }
  if (status === 'failed') {
    return <span className="cert-status-badge cert-status-failed">Failed</span>;
  }
  if (status === 'expired') {
    return <span className="cert-status-badge cert-status-expired">Expired</span>;
  }
  return null;
}

function ActionButton({ 
  cert, 
  onEnroll, 
  onStart, 
  onExam, 
  onPay, 
  onReview, 
  onViewCertificate, 
  enrollingId, 
  startingId, 
  payingId 
}) {
  const { userEnrollmentStatus, certificationId, applicationId } = cert;
  const isEnrolling = enrollingId === certificationId;
  const isStarting = startingId === certificationId;
  const isPaying = payingId === certificationId;

  if (!userEnrollmentStatus) {
    return (
      <button
        className="cert-btn cert-btn-enroll"
        disabled={isEnrolling}
        onClick={(e) => { e.stopPropagation(); onEnroll(certificationId); }}
      >
        {isEnrolling ? 'Enrolling…' : 'Enroll Now'}
      </button>
    );
  }
  if (userEnrollmentStatus === 'unpaid') {
    return (
      <button
        className="cert-btn cert-btn-pay"
        disabled={isPaying}
        onClick={(e) => { e.stopPropagation(); onPay(certificationId, applicationId); }}
      >
        {isPaying ? 'Paying…' : 'Pay Now'}
      </button>
    );
  }
  if (userEnrollmentStatus === 'pending') {
    return (
      <button
        className="cert-btn cert-btn-start"
        disabled={isStarting}
        onClick={(e) => { e.stopPropagation(); onStart(certificationId, applicationId); }}
      >
        {isStarting ? 'Starting…' : 'Start Learning'}
      </button>
    );
  }
  if (userEnrollmentStatus === 'in_progress') {
    return (
      <button
        className="cert-btn cert-btn-exam"
        onClick={(e) => { e.stopPropagation(); onExam(certificationId, applicationId); }}
      >
        Take Exam
      </button>
    );
  }
  if (userEnrollmentStatus === 'failed') {
    return (
      <button
        className="cert-btn cert-btn-failed"
        onClick={(e) => { e.stopPropagation(); onReview(certificationId); }}
      >
        Review Results
      </button>
    );
  }
  if (userEnrollmentStatus === 'completed') {
    return (
      <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
        <button
          className="cert-btn cert-btn-view"
          style={{ flex: 1 }}
          onClick={(e) => { e.stopPropagation(); onViewCertificate(certificationId); }}
        >
          View Certificate
        </button>
        <button
          className="cert-btn"
          style={{ flex: 1, background: 'rgba(255,255,255,0.04)', color: '#e6eaef', border: '1px solid rgba(255,255,255,0.1)' }}
          onClick={(e) => { e.stopPropagation(); onReview(certificationId); }}
        >
          Details
        </button>
      </div>
    );
  }
  return null;
}

export default function CertificationList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const enrollmentMap = useSelector((s) => s.certification.enrollmentMap);

  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrollingId, setEnrollingId] = useState(null);
  const [startingId, setStartingId] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [previewCertId, setPreviewCertId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'error') =>
    setSnackbar({ open: true, message, severity });

  // Merge enrollment state from Redux into list items for reactive button updates
  const mergedCerts = certifications.map((c) => {
    const local = enrollmentMap[c.certificationId];
    if (!local) return c;
    return { ...c, userEnrollmentStatus: local.status, applicationId: local.applicationId };
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getAllCertificationsAuth();
        const list = res?.data ?? res ?? [];
        setCertifications(list);
        dispatch(setEnrollmentMap(list));
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) navigate('/signin');
        else showSnackbar(e?.response?.data?.message || 'Failed to load certifications.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [dispatch, navigate]);

  const handleEnroll = async (certificationId) => {
    setEnrollingId(certificationId);
    try {
      const res = await enrollCertification(certificationId);
      const app = res?.data ?? res;
      dispatch(setEnrollment({
        certificationId,
        status: app?.status ?? 'pending',
        applicationId: app?.applicationId ?? app?.id ?? null,
      }));
      
      if (app?.stripeCheckoutUrl) {
        window.location.href = app.stripeCheckoutUrl;
      } else {
        showSnackbar('Enrolled successfully! Click "Start Learning" to begin.', 'success');
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) showSnackbar(e?.response?.data?.message || 'Already enrolled in this certification.');
      else if (status === 401) navigate('/signin');
      else showSnackbar(e?.response?.data?.message || 'Enrollment failed. Please try again.');
    } finally {
      setEnrollingId(null);
    }
  };

  const handlePay = async (certificationId, applicationId) => {
    setPayingId(certificationId);
    try {
      const successUrl = `${window.location.origin}/payment/success?type=certification&certificationId=${certificationId}&applicationId=${applicationId ?? ''}`;
      const cancelUrl = `${window.location.origin}/payment/cancel?type=certification&certificationId=${certificationId}&applicationId=${applicationId ?? ''}`;
      const res = await createCertificationCheckoutSession(certificationId, successUrl, cancelUrl);
      const checkoutUrl = res?.data ?? res;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        showSnackbar('Failed to initiate payment. Please try again.');
      }
    } catch (e) {
      showSnackbar(e?.response?.data?.message || 'Payment initiation failed.');
    } finally {
      setPayingId(null);
    }
  };

  const handleStart = async (certificationId, applicationId) => {
    setStartingId(certificationId);
    try {
      const res = await startApplication(applicationId);
      const app = res?.data ?? res;
      dispatch(setEnrollment({
        certificationId,
        status: app?.status ?? 'in_progress',
        applicationId: applicationId,
      }));
      navigate(`/certifications/${certificationId}/learn`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) showSnackbar(e?.response?.data?.message || 'Cannot start — application is not in pending state.');
      else if (status === 401) navigate('/signin');
      else showSnackbar(e?.response?.data?.message || 'Failed to start. Please try again.');
    } finally {
      setStartingId(null);
    }
  };

  const handleExam = (certificationId, applicationId) => {
    navigate(`/certifications/${certificationId}/exam/${applicationId}`);
  };

  const handleCardClick = (certificationId) => {
    navigate(`/certifications/${certificationId}`);
  };

  if (loading) {
    return (
      <div className="cert-list-page">
        <div className="cert-list-container">
          <div className="cert-list-header">
            <div className="cert-list-icon-wrap">
              <WorkspacePremiumIcon className="cert-list-icon" />
            </div>
            <div>
              <h1 className="cert-list-title">Certifications</h1>
              <p className="cert-list-subtitle">Earn verified credentials by completing courses and exams</p>
            </div>
          </div>
          <div className="cert-grid">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="cert-card cert-card-skeleton">
                <div className="cert-skeleton cert-skeleton-badge" />
                <div className="cert-skeleton cert-skeleton-title" />
                <div className="cert-skeleton cert-skeleton-meta" />
                <div className="cert-skeleton cert-skeleton-btn" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cert-list-page">
      <div className="cert-list-container">
        {/* Header */}
        <div className="cert-list-header">
          <div className="cert-list-icon-wrap">
            <WorkspacePremiumIcon className="cert-list-icon" />
          </div>
          <div>
            <h1 className="cert-list-title">Certifications</h1>
            <p className="cert-list-subtitle">Earn verified credentials by completing courses and exams</p>
          </div>
        </div>

        {/* Empty state */}
        {mergedCerts.length === 0 && (
          <div className="cert-empty">
            <WorkspacePremiumIcon style={{ fontSize: 48, color: '#3a3f4a', marginBottom: 12 }} />
            <p>No certifications available yet. Check back soon.</p>
          </div>
        )}

        {/* Grid */}
        <div className="cert-grid">
          {mergedCerts.map((cert, idx) => {
            const badgeColor = getBadgeColor(cert.badgeType ?? cert.badgeLevel ?? cert.level ?? '');
            const isTerminal = ['completed', 'failed', 'expired'].includes(cert.userEnrollmentStatus);
            const hasActionFooter = !cert.userEnrollmentStatus || cert.userEnrollmentStatus === 'unpaid';

            return (
              <motion.div
                key={cert.certificationId}
                className={`cert-card${isTerminal ? ' cert-card-terminal' : ''}`}
                style={{ '--badge-color': badgeColor }}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: idx * 0.04 }}
                onClick={() => handleCardClick(cert.certificationId)}
              >
                {/* Top Badge + Status Row */}
                <div className="cert-card-top-row">
                  <div className="cert-card-badge" style={{ background: `${badgeColor}15`, border: `1px solid ${badgeColor}33` }}>
                    <img
                      src={getBadgeIconPath(cert.badgeType ?? cert.badgeLevel ?? cert.level)}
                      alt={`${cert.badgeType ?? cert.badgeLevel ?? cert.level ?? 'Certificate'} Badge`}
                      style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                    />
                  </div>
                  <StatusBadge status={cert.userEnrollmentStatus} />
                </div>

                {/* Name */}
                <h3 className="cert-card-name">{cert.name ?? cert.certificationName}</h3>

                {/* Description */}
                {cert.description && (
                  <p className="cert-card-desc">{cert.description}</p>
                )}

                {/* Meta details bar */}
                <div className="cert-card-meta">
                  <span className="cert-meta-item">
                    <SchoolIcon style={{ fontSize: 13, color: '#8a94a6', marginRight: 3 }} />
                    {cert.totalModules ?? 0} modules
                  </span>
                  <span className="cert-meta-divider">•</span>
                  <span className="cert-meta-item">
                    <QuizIcon style={{ fontSize: 13, color: '#8a94a6', marginRight: 3 }} />
                    {cert.totalQuestions ?? 0} questions
                  </span>
                  <span className="cert-meta-divider">•</span>
                  <span className="cert-meta-item cert-meta-pass">
                    Pass: {cert.passingScore ?? 0}%
                  </span>
                </div>

                {/* Footer action section */}
                {hasActionFooter ? (
                  <div className="cert-card-footer" onClick={(e) => e.stopPropagation()}>
                    <div className="cert-card-price">
                      <span className="price-label">Price</span>
                      <span className="price-value">
                        {cert.price > 0 ? `$${cert.price}` : 'Free'}
                      </span>
                    </div>
                    <div className="cert-card-action">
                      <ActionButton
                        cert={cert}
                        onEnroll={handleEnroll}
                        onStart={handleStart}
                        onExam={handleExam}
                        onPay={handlePay}
                        onReview={handleCardClick}
                        onViewCertificate={setPreviewCertId}
                        enrollingId={enrollingId}
                        startingId={startingId}
                        payingId={payingId}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="cert-card-action-full" onClick={(e) => e.stopPropagation()}>
                    <ActionButton
                      cert={cert}
                      onEnroll={handleEnroll}
                      onStart={handleStart}
                      onExam={handleExam}
                      onPay={handlePay}
                      onReview={handleCardClick}
                      onViewCertificate={setPreviewCertId}
                      enrollingId={enrollingId}
                      startingId={startingId}
                      payingId={payingId}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      <CertificatePreviewModal
        open={previewCertId !== null}
        onClose={() => setPreviewCertId(null)}
        certificationId={previewCertId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
